import { FormEvent, useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import { AcademicProfile, ApiError, api, CalendarData, HomeData, UserDataStatus, WhaleuEntry, WhaleuFeed, WhaleuStats } from '../../lib/api';
import { useShell } from '../../app/shell-context';
import { WhaleuHomeFeed } from '../whaleu/WhaleuHomeFeed';
import { cleanText } from '../whaleu/whaleu-utils';
import { HomeContextOverview } from './HomeContextOverview';
import { FocusHomeCard } from '../focus/FocusHomeCard';

function humanDate(value: string) {
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', month: 'long', day: 'numeric', weekday: 'long',
  }).format(new Date(`${value}T12:00:00+08:00`));
}

export function HomePage() {
  const { openDock, setViewer } = useShell();
  const [home, setHome] = useState<HomeData>();
  const [calendar, setCalendar] = useState<CalendarData>();
  const [userData, setUserData] = useState<UserDataStatus>();
  const [academic, setAcademic] = useState<AcademicProfile>();
  const [whaleu, setWhaleu] = useState<WhaleuFeed>({ backend: '', items: [] });
  const [whaleuStats, setWhaleuStats] = useState<WhaleuStats>();
  const [state, setState] = useState<'loading' | 'ready' | 'guest' | 'error'>('loading');
  const [whaleuState, setWhaleuState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [question, setQuestion] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [homeResult, calendarResult, userDataResult, academicResult, feedResult, statsResult] = await Promise.allSettled([
        api<{ data: HomeData }>('/api/v1/home'),
        api<{ data: CalendarData }>('/api/v1/calendar?includeDone=true'),
        api<{ data: UserDataStatus }>('/api/v1/user-data/status'),
        api<{ data: AcademicProfile }>('/api/v1/profile/academic'),
        api<{ data: WhaleuFeed }>('/api/v1/whaleu/feed?limit=5'),
        api<{ data: WhaleuStats }>('/api/v1/whaleu/stats'),
      ]);
      if (!active) return;

      if (homeResult.status === 'fulfilled') {
        setHome(homeResult.value.data);
        setViewer(homeResult.value.data.viewer);
        setState('ready');
      } else {
        setState(homeResult.reason instanceof ApiError && homeResult.reason.status === 401 ? 'guest' : 'error');
      }
      if (calendarResult.status === 'fulfilled') setCalendar(calendarResult.value.data);
      if (userDataResult.status === 'fulfilled') setUserData(userDataResult.value.data);
      if (academicResult.status === 'fulfilled') setAcademic(academicResult.value.data);
      if (statsResult.status === 'fulfilled') setWhaleuStats(statsResult.value.data);

      if (feedResult.status === 'fulfilled' && feedResult.value.data.items.length) {
        setWhaleu(feedResult.value.data);
        setWhaleuState('ready');
        return;
      }
      try {
        const fallback = await api<{ data: WhaleuFeed }>('/api/v1/whaleu/search?limit=5');
        if (active) { setWhaleu(fallback.data); setWhaleuState('ready'); }
      } catch (error) {
        if (active) {
          setWhaleu((current) => ({ ...current, warning: error instanceof Error ? error.message : '校园信库加载失败' }));
          setWhaleuState('error');
        }
      }
    };
    void load();
    return () => { active = false; };
  }, [setViewer]);

  const overview = home?.overview || { importantEvents: 0, openTodos: 0, unreadNotifications: 0 };
  const focus = home?.focus || [];
  const name = home?.viewer.displayName;
  const dateLine = state === 'ready' && home
    ? `今天是 ${humanDate(home.today)}，先把真正重要的事放在眼前。`
    : state === 'guest'
      ? '登录后，日程、通知、学习与 Agent 会在这里汇合。'
      : state === 'error' ? '上下文暂时未连接，现有应用仍可独立使用。' : '正在连接你的校园生活上下文。';

  const ask = (event: FormEvent) => {
    event.preventDefault();
    const prompt = question.trim();
    if (!prompt) return;
    openDock({ kind: 'agent', prompt });
    setQuestion('');
  };
  const askAboutSignal = (item: WhaleuEntry) => openDock({ kind: 'agent', prompt: `请梳理这条校园信息与我的关系、关键时间和下一步，并标明依据：${cleanText(item.title)} ${item.url || ''}` });

  return <div className="content-column home-page home-page--context">
    <header className="page-header home-page-header">
      <div><p>CAIGEN · PERSONAL CONTEXT</p><h1>{name ? `你好，${name}` : '你好，欢迎来到菜根'}</h1><span>{dateLine}</span></div>
      <div className="header-tools"><span>{home?.today ? humanDate(home.today) : '今日'}</span></div>
    </header>

    <HomeContextOverview home={home} calendar={calendar} userData={userData} academic={academic} whaleu={whaleu} onOpenNotice={() => openDock({ kind: 'evidence', notice: home?.latestNotification || undefined })} />

    <FocusHomeCard suggestedTask={focus[0]?.title} />

    <section className="today-card ornamental-card home-today">
      <header><div className="section-heading compact"><span className="section-glyph ink"><Icon name="spark" /></span><div><span className="section-overline">TODAY · FOCUS</span><h2>今日重点</h2></div></div><a href="/calendar">查看日历 ›</a></header>
      <ol>
        {focus.length ? focus.map((item, index) => <li key={item.id}>
          <time>{item.time}</time><i className={`dot ${['violet', 'red', 'green'][index % 3]}`} />
          <p><b>{item.title}</b><span>{item.subtitle || '来自你的菜根上下文'}</span></p>
          <em className={item.priority === 1 ? 'hot' : ''}>{item.kind === 'event' ? '日程' : '待办'}</em>
        </li>) : <li className="empty-focus"><p><b>{state === 'loading' ? '正在整理今日重点' : '今天暂时没有紧急事项'}</b><span>{state === 'guest' ? '登录后查看你的真实日程与待办。' : '留出一段完整时间，做真正重要的事。'}</span></p></li>}
      </ol>
      <footer><span>{overview.openTodos} 项待办仍在根系中</span><button type="button" onClick={() => openDock({ kind: 'agent', prompt: '根据我的日历和待办，帮我安排今天的优先顺序。' })}>让菜根排优先级</button></footer>
    </section>

    <WhaleuHomeFeed feed={whaleu} stats={whaleuStats} loading={whaleuState === 'loading'} error={whaleuState === 'error' ? whaleu.warning : undefined} onAsk={askAboutSignal} />

    <section className="ask-card ornamental-card home-ask-card">
      <div className="ask-top"><img src="/assets/brand/caigen-root-mark.svg?v=2" alt="" /><p><b>现在，能帮你拆解什么问题。</b><small>菜根会引用你的日程、通知、学习记录与记忆。</small></p></div>
      <div className="ask-suggestions">
        {['帮我梳理今天的优先顺序', '本周有哪些容易错过的截止时间？', '校园信库里有什么与我有关？'].map((item) => <button key={item} onClick={() => setQuestion(item)}>{item}</button>)}
      </div>
      <form className="ask-input" onSubmit={ask}><input value={question} onChange={(event) => setQuestion(event.target.value)} aria-label="向菜根助手提问" placeholder="有什么问题都可以问我…" /><button aria-label="发送"><Icon name="arrow" /></button></form>
      <a className="agent-full-page" href="/agent">进入完整对话页</a>
    </section>
    <p className="page-motto">一根一世界 · 一时一人生</p>
  </div>;
}
